// React
import { FunctionComponent, JSXElementConstructor, ReactElement, useEffect } from "react";
// Components
import { Page, PageConfiguration } from "@fyrstain/hl7-front-library";
// Translation
import i18n from "i18next";
// Authentication
import UserService from "../../services/UserService";
import { toPublicUrl } from "../../services/PublicUrl";

const JupiterPage: FunctionComponent<{
    // The title of the page
    titleKey?: string;
    // The loading state of the page
    loading?: boolean;
    // The content of the page
    children?: ReactElement<any, string | JSXElementConstructor<any>> | undefined;
    // Fit the footer to the bottom of the page
    fitFooter?: boolean;
    // If the page needs login or not
    needsLogin: boolean;
}> = (props) => {

    /////////////////////////////////
    //           METHODS           //
    /////////////////////////////////

    const handleLangChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        i18n.changeLanguage(event.target.value);
    };

    // The navigation component uses "/" for its brand link by default.
    // Point it to Jupiter's home page, including when the application is hosted
    // under a public URL prefix.
    useEffect(() => {
        const navbarLogo = document.querySelector<HTMLAnchorElement>(
            'a.navbar-brand[href="/"]'
        );

        if (navbarLogo) {
            navbarLogo.href = toPublicUrl("/Home");
        }
    });

    const fullConfig: PageConfiguration = {
        // Translation
        language: i18n.t,
        navigationBarConfigs: {
            // Application switcher
            applicationItems: [
                {
                    logoLink: "https://integ.fyrstain.com/assets/ApplicationsLogos/Pandora.png",
                    link: "https://integ.fyrstain.com/Pandora/Home",
                    alt: "Pandora logo",
                },
            ],
            // Application logo
            logoLink: toPublicUrl("/assets/Jupiter.png"),
            alt: "Jupiter logo",
            // Authentication
            authentication: {
              doLogin: UserService.doLogin,
              doLogout: UserService.doLogout,
              isAuthenticated: () => UserService.isAuthenticated() || false,
              getUserName: () => UserService.getUsername(),
            },
            // the menu items with their subItems who contains the navigation to the differents pages
            menuItems: [
                {
                    title: i18n.t('navbar.items.statistics'),
                    link: toPublicUrl("/Statistics"),
                },
            ],
            // the user items 
            // Admin, Login, Logout are the default items
            dropDownItems: [],
            // customItems: <ServerUrlField />
        },
        // the title of the page
        titleKey: props.titleKey,
        // the loading state of the page
        loading: props.loading,
        // the content of the page
        children: props.children,
        // if the page needs login or not
        needsLogin: props.needsLogin,
        isAuthenticated: () => UserService.isAuthenticated() || false,
        doLogin: UserService.doLogin,

        footerConfigs: {
            languages: {
                default: i18n.language,
                onChange: handleLangChange,
                options: [
                    {
                        label: "English",
                        value: "en"
                    },
                    {
                        label: "Français",
                        value: "fr"
                    }
                ]
            },
            logo: [
                {
                    logoLink: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                    alt: "Horizontal logo type",
                    link: "/Home"
                },
                {
                    logoLink: (process.env.PUBLIC_URL ?? "") + "/assets/logo-OVH.svg",
                    alt: "Logo OVH"
                },
                ...(process.env.REACT_APP_DISPLAY_CLIENT_LOGO === 'true' ?
                    [
                        {
                            logoLink: "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
                            alt: "Horizontal logo type",
                            link: (process.env.PUBLIC_URL ?? "") + "/Home"
                        },
                        {
                            logoLink: (process.env.PUBLIC_URL ?? "") + "/assets/client_logo.jpg",
                            alt: "HL7 Europe logo"
                        }
                    ]
                    : [])
            ],
            items: [
                {
                    label: i18n.t('footer.items.about'),
                    link: toPublicUrl("/InProgress")
                },
                {
                    label: i18n.t('footer.items.contact'),
                    link: toPublicUrl("/InProgress")
                },
                {
                    label: i18n.t('footer.items.problemtracking'),
                    link: toPublicUrl("/InProgress")
                },
                {
                    label: i18n.t("footer.items.implementationguide"),
                    link: toPublicUrl("/ImplementationGuide"),
                },
            ]
        }
    };

    return (
    <div className={props.fitFooter ? "jupiter-page fit-footer" : "jupiter-page"}>
        <Page {...fullConfig} />
    </div>
);
};

export default JupiterPage;
